import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, LinearProgress,
  Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tooltip, Typography, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import {
  ArrowOutward, CheckCircleOutline, DownloadOutlined,
  EventAvailableOutlined, MailOutline, ReceiptLongOutlined, Refresh,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CustomDatePicker } from "../../../components/Custom/DatePicker";
import { financialDashboard, FinanceFilters, FinanceReport, FinanceShow, FinanceStatus } from "../../../api/financialDashboard";
import { StyledPaginationItem } from "../_components/dashboardStyles";
import { outlinedIconSx, SEVERITY_COLORS, THEME } from "../overview/boardStyles";
import { currentFinanceMonth, financeMonthRange, financeDateRangeError, FinanceControls, FINANCE_STATUS_OPTIONS } from "./financialDashboard.utils";
import { coordinatorFilterToParams, CoordinatorFilterValue } from "../_components/dashboardCoordinators";
import FinancialCloseoutPanel from "../../event-details/components/FinancialCloseoutPanel";
import FinancialResultsPanel from "./FinancialResultsPanel";

const STATUS = FINANCE_STATUS_OPTIONS;
const dateLabel = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", {
  timeZone: "UTC", month: "short", day: "numeric", year: "numeric",
});
const timeLabel = (value: string | null) => value ? new Date(value).toLocaleString("en-US", {
  dateStyle: "medium", timeStyle: "short",
}) : "—";

function StatusChip({ status }: { status: FinanceStatus }) {
  return <Chip size="small" variant="outlined" label={STATUS[status].label}
    sx={{ color: STATUS[status].color, borderColor: STATUS[status].color, fontWeight: 500 }} />;
}

type Props = {
  controls: FinanceControls;
  onControlsChange: (next: FinanceControls) => void;
  search?: string;
  coordinatorFilter?: CoordinatorFilterValue;
  showDuplicates?: boolean;
  onOpenShow: (show: FinanceShow) => void;
  canViewCloseout?: boolean;
};

export default function DashboardFinance({ controls, onControlsChange, search = "", coordinatorFilter, showDuplicates = false, onOpenShow, canViewCloseout = false }: Props) {
  const [pageSelection, setPageSelection] = useState({ key: "", page: 1 });
  const [report, setReport] = useState<FinanceReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exportError, setExportError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState<FinanceShow | null>(null);
  const [closeoutShow, setCloseoutShow] = useState<FinanceShow | null>(null);
  const [mode, setMode] = useState<'followup' | 'results'>('followup');
  const showingResults = canViewCloseout && mode === 'results';
  const rangeError = financeDateRangeError(controls.from, controls.to);
  const baseFilters = useMemo(() => ({
    from: controls.from, to: controls.to, status: controls.status, archivedOnly: controls.archivedOnly,
    search, duplicates: showDuplicates, ...coordinatorFilterToParams(coordinatorFilter),
  }), [controls.from, controls.to, controls.status, controls.archivedOnly, search, showDuplicates, coordinatorFilter]);
  const filterKey = JSON.stringify(baseFilters);
  const filters = useMemo<FinanceFilters>(() => ({ ...baseFilters,
    page: pageSelection.key === filterKey ? pageSelection.page : 1,
  }), [baseFilters, filterKey, pageSelection]);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    setExportError("");
    setReport(null);
    if (rangeError || showingResults) {
      setLoading(false);
      return;
    }
    financialDashboard.list(filters).then(data => {
      if (current) setReport(data);
    }).catch(e => {
      if (current) setError(e instanceof Error ? e.message : "Could not load invoice follow-up.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [filters, refresh, rangeError, showingResults]);

  const updateStatus = (status: FinanceFilters["status"]) => onControlsChange({ ...controls, status });
  const summary = report?.summary;
  const cards = [
    { label: "Ended shows", value: summary?.total, detail: "Filtered shows in this range", icon: <EventAvailableOutlined />, color: THEME.pink, status: "all" as const },
    { label: "No final invoice", value: summary?.no_invoice, detail: "Pending invoice review", icon: <ReceiptLongOutlined />, color: SEVERITY_COLORS.warning, status: "no_invoice" as const },
    { label: "No send recorded", value: summary?.unconfirmed, detail: `${summary?.queued ?? 0} queued · ${summary?.failed ?? 0} failed`, icon: <MailOutline />, color: THEME.pink, status: "unconfirmed" as const },
    { label: "Send recorded", value: summary?.sent, detail: "All saved invoices sent", icon: <CheckCircleOutline />, color: THEME.green, status: "sent" as const },
  ];
  const exportReport = async () => {
    setExporting(true);
    setExportError("");
    try { await financialDashboard.export(filters); }
    catch (e) { setExportError(e instanceof Error ? e.message : "Could not export the report."); }
    finally { setExporting(false); }
  };

  return <Box sx={{ p: { xs: "0 20px 32px", sm: "0 40px 48px" }, color: THEME.white }}>
    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "center" }} gap={2} mb={3}>
      <Box>
        <Typography variant="h4" component="h2">Finance</Typography>
        <Typography sx={{ color: THEME.muted, mt: 0.5 }}>Ended shows and final invoice follow-up</Typography>
      </Box>
      {!showingResults && <Stack direction="row" alignItems="center" gap={1}>
        <Tooltip title="Refresh invoice and email records">
          <span><IconButton aria-label="Refresh finance" onClick={() => setRefresh(n => n + 1)}
            disabled={loading} sx={{ ...outlinedIconSx, width: 40, height: 40,
              "&.Mui-disabled": { color: THEME.faint, borderColor: THEME.pinkSoft } }}><Refresh /></IconButton></span>
        </Tooltip>
        <Button variant="outlined" color="secondary"
          sx={{ height: 40, color: THEME.white, borderColor: THEME.pink, borderRadius: "8px",
            bgcolor: THEME.black, whiteSpace: "nowrap",
            "&:hover": { borderColor: THEME.pink, bgcolor: THEME.pinkTint },
            "&.Mui-disabled": { color: THEME.faint, borderColor: THEME.pinkSoft, bgcolor: THEME.line } }}
          startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlined />}
          disabled={loading || exporting || !!rangeError || !report?.pagination.total} onClick={exportReport}>
          {exporting ? "Exporting…" : "Export CSV"}
        </Button>
      </Stack>}
    </Stack>

    {canViewCloseout && <ToggleButtonGroup exclusive value={showingResults ? 'results' : 'followup'} onChange={(_, value) => value && setMode(value)} aria-label="Finance report" sx={{mb:3,display:'flex',flexWrap:'wrap','& .MuiToggleButton-root':{color:`${THEME.white} !important`,borderColor:THEME.pink,textTransform:'none'},'& .MuiToggleButton-root.Mui-selected':{bgcolor:`${THEME.pinkTint} !important`,color:`${THEME.white} !important`}}}>
      <ToggleButton value="followup">Invoice follow-up</ToggleButton><ToggleButton value="results">Financial results</ToggleButton>
    </ToggleButtonGroup>}

    <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between"
      alignItems={{ xs: "stretch", lg: "center" }} gap={2} mb={3}>
      <Box>
        <Typography variant="body2" sx={{ color: THEME.muted, mb: 1.5 }}>Show end date range</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
          <Stack direction="row" alignItems="center" gap={1} sx={{ flexWrap: "wrap", rowGap: 2 }}>
            {(["from", "to"] as const).map(boundary => <Box key={boundary}
              sx={{ width: { xs: "100%", sm: 190 }, minWidth: 0, height: 40, flexShrink: 0,
                "& .MuiTextField-root, & .MuiInputBase-root": { height: "100%" } }}>
              <CustomDatePicker label={boundary === "from" ? "From" : "To"}
                format="MM/DD/YYYY" value={controls[boundary] ? dayjs(controls[boundary]) : null}
                onChange={(date: ReturnType<typeof dayjs> | null) => {
                  onControlsChange({ ...controls, [boundary]: date?.isValid() ? date.format("YYYY-MM-DD") : "" });
                }}
                slotProps={{ textField: { size: "small" } }} />
            </Box>)}
            <Button size="small" sx={{ height: 40, whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={() => onControlsChange({ ...controls, ...financeMonthRange(currentFinanceMonth()) })}>This month</Button>
          </Stack>
        </LocalizationProvider>
      </Box>
      <Typography variant="body2" sx={{ color: THEME.muted }}>
        {rangeError ? "Select a valid date range" : `${dateLabel(filters.from)} – ${dateLabel(filters.to)}`}
        {report?.scope === "mine" ? " · My assigned shows" : ""}
      </Typography>
    </Stack>

    {showingResults ? <FinancialResultsPanel filters={filters} rangeError={rangeError} refreshKey={refresh} onRefresh={() => setRefresh(n => n + 1)} onCloseout={setCloseoutShow} onOpenShow={onOpenShow} /> : <>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
      {cards.map(card => <Box key={card.label} component="button" type="button"
        onClick={() => updateStatus(card.status)} aria-pressed={filters.status === card.status}
        sx={{
          all: "unset", boxSizing: "border-box", cursor: "pointer", minWidth: 0,
          background: THEME.black, border: `1px solid ${filters.status === card.status ? THEME.pink : THEME.pinkSoft}`,
          borderLeft: `5px solid ${card.color}`, borderRadius: "8px", p: { xs: 1.5, sm: 2 },
          boxShadow: filters.status === card.status ? THEME.glow : "none",
          "&:hover": { borderColor: THEME.pink, boxShadow: THEME.glow },
          "&:focus-visible": { outline: `2px solid ${THEME.pink}`, outlineOffset: 2 },
        }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography sx={{ fontSize: "15px" }}>{card.label}</Typography>
          <Box sx={{ color: card.color, display: { xs: "none", sm: "flex" } }}>{card.icon}</Box>
        </Stack>
        <Typography variant="h3" sx={{ color: card.color, my: 0.5 }}>{loading ? "—" : card.value ?? "—"}</Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px" }}>{card.detail}</Typography>
      </Box>)}
    </Box>

    <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px", mb: 2 }}>
      {controls.archivedOnly ? "Archived shows only." : "Includes archived shows."} Shows appear after their final day ends in the show’s timezone.
      {showDuplicates ? " Duplicates are included and marked." : ""}
    </Typography>

    {(rangeError || error || exportError) && <Alert severity="error" sx={{ mb: 2 }}>{rangeError || error || exportError}</Alert>}
    <Box sx={{ border: `1px solid ${THEME.pink}`, borderRadius: "12px", overflow: "hidden", bgcolor: THEME.black }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} sx={{ p: 2 }}>
        <Typography variant="h5">Ended shows · {report?.pagination.total ?? "—"}</Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px" }}>
          {report ? `Updated ${timeLabel(report.generatedAt)}` : error || rangeError ? "Report unavailable" : "Checking invoices and email records"}
        </Typography>
      </Stack>
      {loading && <LinearProgress color="secondary" aria-label="Loading invoice follow-up" />}
      <TableContainer className="custom-scroll-design">
        <Table size="small" sx={{
          minWidth: 800,
          "& .MuiTableCell-root": { color: THEME.white, borderBottom: `1px solid ${THEME.pinkSoft}`, py: 1.5, px: 2, fontSize: "15px" },
          "& .MuiTableCell-head": { bgcolor: THEME.pink, borderColor: THEME.pink, fontWeight: 500, whiteSpace: "nowrap" },
          "& tbody tr:hover": { bgcolor: THEME.rowHover },
        }}>
          <TableHead><TableRow>
            <TableCell>Show / Ended</TableCell><TableCell>Final Invoice</TableCell>
            <TableCell>Sending Status</TableCell><TableCell>Recipients</TableCell><TableCell align="right">Review</TableCell>
          </TableRow></TableHead>
          <TableBody>{(Array.isArray(report?.rows) ? report.rows : []).map(row => {
            const recipients = [...new Set(row.invoices.flatMap(invoice => invoice.to))];
            const sent = row.invoices.filter(invoice => invoice.sentAt)
              .sort((a, b) => (b.sentAt ?? "").localeCompare(a.sentAt ?? ""))[0];
            return <TableRow key={row._id}>
              <TableCell sx={{ maxWidth: 360 }}>
                <Typography sx={{ fontWeight: 500, textDecoration: row.isDuplicate ? "line-through" : "none" }}>{row.eventName}</Typography>
                <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px", mt: 0.5 }}>
                  {row.jobNumber || "No job number"}{row.location ? ` · ${row.location}` : ""}
                </Typography>
                <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px", mt: 0.5 }}>
                  {dateLabel(row.endDay)}{row.isArchived ? " · Archived" : ""}{row.isDuplicate ? " · Duplicate" : ""}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography>{row.invoices.length === 1 ? row.invoices[0].number || "Final invoice" : row.invoices.length ? `${row.invoices.length} final invoices` : "Not created"}</Typography>
                {row.invoices.length > 0 && <Button variant="text" size="small" onClick={() => setSelected(row)}
                  sx={{ p: 0, mt: 0.5, minWidth: 0, textTransform: "none" }}>Sending details</Button>}
              </TableCell>
              <TableCell>
                <StatusChip status={row.status} />
                <Typography variant="body2" sx={{ fontSize: "14px", color: THEME.muted, mt: 1 }}>
                  {row.status === "sent" ? timeLabel(sent?.sentAt ?? null) : row.invoices.length
                    ? `${row.invoices.filter(invoice => invoice.status === "sent").length} of ${row.invoices.length} with a send recorded`
                    : "Pending invoice review"}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 260 }}>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{recipients.slice(0, 2).join(", ") || "—"}</Typography>
                {recipients.length > 2 && <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px" }}>+{recipients.length - 2} more in sending details</Typography>}
              </TableCell>
              <TableCell align="right">
                {canViewCloseout && <Button variant="outlined" size="small" onClick={() => setCloseoutShow(row)} sx={{ color: THEME.white, borderColor: THEME.pink, textTransform: "none", minHeight: 40, px: 2, whiteSpace: "nowrap", "&:hover": { borderColor: THEME.pink, bgcolor: THEME.pinkTint } }}>Closeout</Button>}
                <Tooltip title="Open show"><IconButton aria-label={`Open ${row.eventName}`} onClick={() => onOpenShow(row)}
                  sx={{ color: THEME.pink }}><ArrowOutward fontSize="small" /></IconButton></Tooltip>
              </TableCell>
            </TableRow>;
          })}</TableBody>
        </Table>
      </TableContainer>
      {!loading && !error && !rangeError && !report?.rows?.length && <Box sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
        <EventAvailableOutlined sx={{ color: THEME.pink, fontSize: 32, mb: 1 }} />
        <Typography>No ended shows match these dates and filters.</Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, mt: 1 }}>Choose another date range or invoice status.</Typography>
      </Box>}
      {!!report?.pagination.total && <Stack direction={{ xs: "column", sm: "row" }} alignItems="center"
        justifyContent="space-between" gap={1} p={2}>
        <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px" }}>
          Export includes all {report.pagination.total} matching shows, across every page.
        </Typography>
        <Pagination page={report.pagination.page} count={report.pagination.totalPages}
          onChange={(_, page) => setPageSelection({ key: filterKey, page })}
          renderItem={item => <StyledPaginationItem {...item} />} />
      </Stack>}
    </Box>
    <Typography variant="body2" sx={{ color: THEME.muted, fontSize: "14px", mt: 2 }}>
      Sending status follows the latest saved invoice per configuration. A recorded send confirms transport acceptance, not delivery or reading. Older invoices without linked email history remain unconfirmed.
    </Typography>
    </>}

    <Dialog open={canViewCloseout && !!closeoutShow} onClose={() => { setCloseoutShow(null); setRefresh(n => n + 1); }} fullWidth maxWidth="lg"
      aria-label={`Financial closeout${closeoutShow ? ` · ${closeoutShow.eventName}` : ''}`}
      PaperProps={{ sx: { bgcolor: THEME.black, color: THEME.white, border: `2px solid ${THEME.pink}`, borderRadius: '16px', m: 2, width: 'calc(100% - 32px)', maxHeight: 'calc(100dvh - 32px)', overflow: 'hidden' } }}>
      {canViewCloseout && closeoutShow && <FinancialCloseoutPanel eventId={closeoutShow._id} eventName={closeoutShow.eventName} layout="dialog" onClose={() => { setCloseoutShow(null); setRefresh(n => n + 1); }} />}
    </Dialog>
    <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="md"
      PaperProps={{ sx: { bgcolor: THEME.black, color: THEME.white, border: `2px solid ${THEME.pink}`, borderRadius: "16px" } }}>
      <DialogTitle>
        Invoice sending details
        <Typography sx={{ color: THEME.muted, mt: 0.5 }}>{selected?.eventName}</Typography>
      </DialogTitle>
      <DialogContent>{selected?.invoices.map(invoice => <Box key={invoice._id}
        sx={{ border: `1px solid ${THEME.pinkSoft}`, borderRadius: "8px", p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" gap={1}>
          <Typography sx={{ fontWeight: 500 }}>{invoice.number || "Final invoice"}</Typography>
          <StatusChip status={invoice.status} />
        </Stack>
        <Typography variant="body2" sx={{ color: THEME.muted, mt: 1 }}>{invoice.configuration} · Created {timeLabel(invoice.createdAt)}</Typography>
        <Divider sx={{ borderColor: THEME.pinkSoft, my: 2 }} />
        <Typography sx={{ overflowWrap: "anywhere" }}>To: {invoice.to.join(", ") || "No recipient recorded"}</Typography>
        <Typography sx={{ color: THEME.muted, mt: 1, overflowWrap: "anywhere" }}>CC: {invoice.cc.join(", ") || "—"}</Typography>
        <Typography sx={{ mt: 2 }}>Send recorded: {timeLabel(invoice.sentAt)}</Typography>
        <Typography sx={{ color: THEME.muted, mt: 1 }}>Latest email record: {timeLabel(invoice.lastAttemptAt)}{invoice.lastAttemptStatus ? ` · ${invoice.lastAttemptStatus}` : ""}</Typography>
      </Box>)}</DialogContent>
      <DialogActions sx={{ p: 2 }}><Button onClick={() => setSelected(null)}>Close</Button></DialogActions>
    </Dialog>
  </Box>;
}
